/**
 * GlyphButton V2 — Premium button with smooth transitions and glass hover.
 */

import { type ReactNode, type ButtonHTMLAttributes } from "react";

interface GlyphButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  icon?: ReactNode;
}

export function GlyphButton({
  children,
  variant = "primary",
  size = "md",
  icon,
  className = "",
  disabled,
  ...props
}: GlyphButtonProps) {
  const sizeClasses = {
    sm: "px-3 py-1.5 text-[11px] gap-1.5",
    md: "px-4 py-2 text-xs gap-2",
    lg: "px-6 py-2.5 text-sm gap-2",
  };

  const variantClasses = {
    primary: `
      bg-[var(--bw-accent)] text-[var(--bw-bg)] font-semibold
      border-[var(--bw-accent)]
      hover:shadow-[var(--bw-glow-accent)]
      hover:brightness-110
    `,
    secondary: `
      bg-[var(--bw-glass-bg)] text-[var(--bw-fg)]
      border-[var(--bw-glass-border)]
      hover:border-[var(--bw-accent)] hover:text-[var(--bw-accent)]
      hover:shadow-[var(--bw-glow)]
    `,
    danger: `
      bg-transparent text-[var(--bw-danger)]
      border-[var(--bw-danger)]/40
      hover:bg-[var(--bw-danger)]/10
      hover:border-[var(--bw-danger)]
      hover:shadow-[0_0_20px_rgba(255,23,68,0.2)]
    `,
    ghost: `
      bg-transparent text-[var(--bw-muted)]
      border-transparent
      hover:text-[var(--bw-fg)] hover:bg-[var(--bw-glass-bg)]
      hover:border-[var(--bw-glass-border)]
    `,
  };

  const clipSize = size === "sm" ? "6px" : "8px";

  return (
    <button
      className={`
        relative inline-flex items-center justify-center
        font-mono uppercase tracking-[0.15em]
        border
        transition-all duration-[var(--bw-transition-fast)]
        disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none
        active:scale-[0.97] active:duration-75
        backdrop-blur-sm
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `}
      disabled={disabled}
      style={{
        clipPath: `polygon(0 0, calc(100% - ${clipSize}) 0, 100% ${clipSize}, 100% 100%, ${clipSize} 100%, 0 calc(100% - ${clipSize}))`,
      }}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
}
